import {
  cn,
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
  InputGroupAddon,
  InputGroupButton,
  useComboboxAnchor,
} from '@foresightdata/web-common-components';
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
  Button as MuiButton,
  createFilterOptions,
  TextField as MuiTextField,
} from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Plus } from 'lucide-react';
import {
  CSSProperties,
  FocusEvent,
  FocusEventHandler,
  KeyboardEvent,
  KeyboardEventHandler,
  ReactNode,
  Ref,
  useId,
  useMemo,
  useState,
} from 'react';
import { MaggieFeatureFlags } from '../../util/feature-flags';

/**
 * The app's Autocomplete (FD-6105). Renders MUI's Autocomplete, or, when the `useV2Components`
 * flag is on, web-common's Combobox (Base UI). Call sites switch to this wrapper; the flag is
 * the only switch.
 *
 * Deliberately smaller and flatter than MUI: only the props the app uses, no event in
 * `onChange`, `renderOption` returns the row's content (the wrapper owns the row), `filterOptions`
 * gets the typed text rather than MUI's state object, the input's props are top level instead
 * of a `renderInput` callback, and the "add a new …" row is a declared `action` rather than an
 * option smuggled in through `filterOptions`.
 */
export type AutocompleteValue<T, Multiple extends boolean | undefined> = Multiple extends true
  ? T[]
  : T | null;

export interface AutocompleteAction {
  /** Row text, followed by the typed text when there is some ("Add fund Growth II"). */
  label: string;
  /** Shown above the list. */
  helperText?: string;
  onAction: (inputValue?: string) => void;
}

export interface AutocompleteProps<T, Multiple extends boolean | undefined = false> {
  options: readonly T[];
  value: AutocompleteValue<T, Multiple>;
  onChange: (value: AutocompleteValue<T, Multiple>) => void;
  multiple?: Multiple;
  getOptionLabel?: (option: T) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  getOptionDisabled?: (option: T) => boolean;
  groupBy?: (option: T) => string;
  /** Replaces the default contains-filter; runs on the typed text. Also where a limit is applied. */
  filterOptions?: (options: T[], inputValue: string) => T[];
  /** The row's content; the wrapper renders the row itself. */
  renderOption?: (option: T, state: { selected: boolean; inputValue: string }) => ReactNode;
  /** The picks shown in the input (multiple); defaults to one removable chip per pick. */
  renderTags?: (values: T[]) => ReactNode;
  action?: AutocompleteAction;
  /** Controlled typed text. */
  inputValue?: string;
  onInputChange?: (inputValue: string) => void;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  loading?: boolean;
  noOptionsText?: ReactNode;
  disabled?: boolean;
  disableClearable?: boolean;
  disableCloseOnSelect?: boolean;
  /** Accept typed text that is not an option (MUI); v2 needs the caller to offer it as an option. */
  freeSolo?: boolean;
  /** v1: a 15rem floor on the list, for an input too narrow to show long options. */
  wideDropdown?: boolean;
  /** As MUI: focusing the input opens the list. */
  openOnFocus?: boolean;
  /** As MUI: focusing the input selects its text (default: unless `freeSolo`). */
  selectOnFocus?: boolean;
  autoHighlight?: boolean;
  // input
  placeholder?: string;
  label?: ReactNode;
  helperText?: ReactNode;
  error?: boolean;
  autoFocus?: boolean;
  name?: string;
  id?: string;
  'aria-label'?: string;
  /** Inside the input, right-aligned, ahead of the clear / open controls. */
  inputEndAdornment?: ReactNode;
  inputRef?: Ref<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  /** On the input. Stopping propagation also stops the component's own key handling. */
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  /** The root element. */
  ref?: Ref<HTMLDivElement>;
  className?: string;
  style?: CSSProperties;
  fullWidth?: boolean;
  /** Accepted for v1; v2 renders every field at the theme's (small) size. */
  size?: 'small' | 'medium';
}

// The action row travels through the option list so it takes part in keyboard navigation (as
// MUI's own options do); it is never a value.
const ACTION = Symbol('autocomplete-action');
type ActionOption = { [ACTION]: true; inputValue: string };
const isActionOption = (option: unknown): option is ActionOption =>
  typeof option === 'object' && option !== null && ACTION in option;

export function Autocomplete<T, Multiple extends boolean | undefined = false>(
  props: AutocompleteProps<T, Multiple>
) {
  const { useV2Components } = useFlags<MaggieFeatureFlags>();
  return useV2Components ? <V2Autocomplete {...props} /> : <MuiAutocompleteV1 {...props} />;
}

type Row<T> = T | ActionOption;

function defaultLabel(option: unknown) {
  return typeof option === 'string' ? option : String(option ?? '');
}

/* ------------------------------------ v1: MUI ------------------------------------ */

function MuiAutocompleteV1<T, Multiple extends boolean | undefined>({
  options,
  value,
  onChange,
  multiple,
  getOptionLabel = defaultLabel,
  isOptionEqualToValue,
  getOptionDisabled,
  groupBy,
  filterOptions,
  renderOption,
  renderTags,
  action,
  inputValue,
  onInputChange,
  open,
  onOpen,
  onClose,
  loading,
  noOptionsText,
  disabled,
  disableClearable,
  disableCloseOnSelect,
  freeSolo,
  wideDropdown,
  openOnFocus,
  selectOnFocus,
  autoHighlight,
  placeholder,
  label,
  helperText,
  error,
  autoFocus,
  name,
  id,
  'aria-label': ariaLabel,
  inputEndAdornment,
  inputRef,
  onBlur,
  onFocus,
  onKeyDown,
  ref,
  className,
  style,
  fullWidth,
  size,
}: AutocompleteProps<T, Multiple>) {
  const hasPicks = multiple && Array.isArray(value) && value.length > 0;
  const defaultFilter = useMemo(() => createFilterOptions<Row<T>>(), []);
  // With `freeSolo` MUI also hands typed strings here; the app's label functions cope with them.
  const rowLabel = (row: Row<T> | string) => (isActionOption(row) ? '' : getOptionLabel(row as T));

  const filter: MuiAutocompleteProps<Row<T>, Multiple, boolean, boolean>['filterOptions'] = (rows, state) => {
    const plain = rows.filter((row): row is T => !isActionOption(row));
    const filtered: Row<T>[] = filterOptions
      ? filterOptions(plain, state.inputValue)
      : defaultFilter(plain, { ...state, getOptionLabel: rowLabel });
    if (action) {
      filtered.unshift({ [ACTION]: true, inputValue: state.inputValue });
    }
    return filtered;
  };

  return (
    <MuiAutocomplete<Row<T>, Multiple, boolean, boolean>
      ref={ref}
      className={className}
      style={style}
      fullWidth={fullWidth}
      size={size}
      options={options}
      value={value as MuiAutocompleteProps<Row<T>, Multiple, boolean, boolean>['value']}
      onChange={(_event, newValue) => {
        // The action row is never a value, in either mode (v2 does the same below).
        const picked = multiple ? (newValue as Row<T>[]) : [newValue as Row<T>];
        const actionRow = picked.find(isActionOption);
        if (actionRow) {
          action?.onAction(actionRow.inputValue || undefined);
          return;
        }
        onChange(newValue as AutocompleteValue<T, Multiple>);
      }}
      multiple={multiple}
      getOptionLabel={rowLabel}
      isOptionEqualToValue={
        isOptionEqualToValue
          ? (option, current) =>
              !isActionOption(option) && !isActionOption(current) && isOptionEqualToValue(option, current)
          : undefined
      }
      getOptionDisabled={
        getOptionDisabled ? (option) => !isActionOption(option) && getOptionDisabled(option) : undefined
      }
      groupBy={groupBy ? (option) => (isActionOption(option) ? '' : groupBy(option)) : undefined}
      filterOptions={filter}
      renderOption={(liProps, option, state) => {
        const { key, ...rest } = liProps as typeof liProps & { key: string };
        if (isActionOption(option)) {
          return (
            <MuiButton
              key={key}
              {...rest}
              component='li'
              fullWidth
              sx={{
                justifyContent: 'start',
                pl: '1.125em',
                mt: '-0.5rem',
                // The Button's own background hides the list's highlight; reapply the option's.
                '&.Mui-focused': { bgcolor: 'action.hover' },
                '&.Mui-focusVisible': { bgcolor: 'action.focus' },
              }}
              color='secondary'
              size='medium'
            >
              {`${action!.label}${option.inputValue ? ` ${option.inputValue}` : ''}`}
            </MuiButton>
          );
        }
        // MUI keys a row by its label, so two same-named rows (SAFE rounds) would share a key and
        // a stale row would survive filtering; key primitives by value, objects by position too.
        return (
          <li key={typeof option === 'object' ? `${key}-${state.index}` : String(option)} {...rest}>
            {renderOption
              ? renderOption(option, { selected: state.selected, inputValue: state.inputValue })
              : getOptionLabel(option)}
          </li>
        );
      }}
      renderTags={
        renderTags ? (values) => renderTags(values.filter((v): v is T => !isActionOption(v))) : undefined
      }
      inputValue={inputValue}
      onInputChange={onInputChange ? (_event, text) => onInputChange(text) : undefined}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      loading={loading}
      noOptionsText={noOptionsText}
      disabled={disabled}
      disableClearable={disableClearable}
      disableCloseOnSelect={disableCloseOnSelect}
      freeSolo={freeSolo}
      slotProps={
        wideDropdown ? { popper: { style: { minWidth: '15rem' }, placement: 'bottom-start' } } : undefined
      }
      openOnFocus={openOnFocus}
      selectOnFocus={selectOnFocus}
      autoHighlight={autoHighlight}
      onBlur={onBlur as FocusEventHandler<HTMLDivElement>}
      onFocus={onFocus as FocusEventHandler<HTMLDivElement>}
      componentsProps={action ? { paper: getSelectPaperProps(action.helperText) } : undefined}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          id={id ?? params.id}
          name={name}
          label={label}
          helperText={helperText}
          error={error}
          // The ghost input shows its placeholder even when picks render as chips beside it.
          placeholder={hasPicks ? undefined : placeholder}
          autoFocus={autoFocus}
          inputRef={inputRef}
          // `params.inputProps` carries Autocomplete's own wiring; the name goes on the input, where
          // the combobox role is, and so does `onKeyDown`, as in v2. Autocomplete handles keys on its
          // root, so stopping propagation here still stops that.
          slotProps={{
            htmlInput: { ...params.inputProps, 'aria-label': ariaLabel, onKeyDown },
            input: inputEndAdornment
              ? {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {inputEndAdornment}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }
              : params.InputProps,
          }}
        />
      )}
    />
  );
}

/** MUI paper styles for a list with an action row and its helper text. */
export function getSelectPaperProps(helperText?: string) {
  return {
    sx: {
      minWidth: 'max-content',
      paddingTop: helperText ? '0.5rem' : '0.2rem',
      ':before': helperText
        ? {
            // A CSS string: quotes and line breaks in the text are escaped.
            content: JSON.stringify(helperText),
            transform: { translate: '0, 1rem' },
            marginLeft: '1rem',
            fontSize: '0.75rem',
            color: 'text.secondary',
          }
        : undefined,
    },
  };
}

/* --------------------------------- v2: web-common --------------------------------- */

function V2Autocomplete<T, Multiple extends boolean | undefined>({
  options,
  value,
  onChange,
  multiple,
  getOptionLabel = defaultLabel,
  isOptionEqualToValue = Object.is,
  getOptionDisabled,
  groupBy,
  filterOptions,
  renderOption,
  renderTags,
  action,
  inputValue: inputValueProp,
  onInputChange,
  open,
  onOpen,
  onClose,
  loading,
  noOptionsText = 'No options',
  disabled,
  disableClearable,
  disableCloseOnSelect,
  freeSolo,
  openOnFocus,
  selectOnFocus = !freeSolo,
  placeholder,
  label,
  helperText,
  error,
  autoFocus,
  name,
  id: idProp,
  'aria-label': ariaLabel,
  inputEndAdornment,
  inputRef,
  onBlur,
  onFocus,
  onKeyDown,
  ref,
  className,
  style,
  fullWidth = true,
  autoHighlight,
}: AutocompleteProps<T, Multiple>) {
  const generatedId = useId();
  // Base UI's open state, held here so focus can open the list (MUI's `openOnFocus`) unless the
  // caller controls `open`.
  const [innerOpen, setInnerOpen] = useState(false);
  const isOpen = open ?? innerOpen;
  const id = idProp ?? generatedId;
  const helperId = helperText ? `${id}-helper` : undefined;
  const anchor = useComboboxAnchor();
  // The typed text, for filtering. Base UI keeps the input itself (it shows the pick's label
  // when closed) unless the caller controls `inputValue`.
  const [typed, setTyped] = useState('');
  const inputValue = inputValueProp ?? typed;

  const selected = useMemo<T[]>(
    () => (multiple ? (value as T[]) : value == null ? [] : [value as T]),
    [multiple, value]
  );
  // With the default identity comparison a Set keeps long lists linear.
  const selectedSet = useMemo(() => new Set<T>(selected), [selected]);
  const isSelected = (option: T) =>
    isOptionEqualToValue === Object.is
      ? selectedSet.has(option)
      : selected.some((current) => isOptionEqualToValue(option, current));

  // MUI filters the whole list in one go (and that is where the app caps long lists), so the
  // wrapper filters and hands Base UI the result.
  const filtered = useMemo<Row<T>[]>(() => {
    // As in MUI: text that is just the current pick's label is not a search.
    const pick = !multiple && value != null ? (value as T) : null;
    const text = pick && inputValue === getOptionLabel(pick) ? '' : inputValue;
    const query = text.trim().toLowerCase();
    const plain = filterOptions
      ? filterOptions([...options], text)
      : options.filter((option) => getOptionLabel(option).toLowerCase().includes(query));
    const rows: Row<T>[] = [...plain];
    if (action) {
      rows.unshift({ [ACTION]: true, inputValue });
    }
    return rows;
  }, [action, filterOptions, getOptionLabel, inputValue, multiple, options, value]);

  // The picked option must stay in the list for Base UI to show its label.
  const items = useMemo<Row<T>[]>(() => {
    const present = isOptionEqualToValue === Object.is ? new Set<Row<T>>(filtered) : null;
    const missing = selected.filter((current) =>
      present
        ? !present.has(current)
        : !filtered.some((row) => !isActionOption(row) && isOptionEqualToValue(row, current))
    );
    return missing.length ? [...filtered, ...missing] : filtered;
  }, [filtered, isOptionEqualToValue, selected]);

  const groups = useMemo(() => {
    if (!groupBy) {
      return null;
    }
    const byGroup = new Map<string, Row<T>[]>();
    for (const row of items) {
      const key = isActionOption(row) ? '' : groupBy(row);
      byGroup.set(key, [...(byGroup.get(key) ?? []), row]);
    }
    // Base UI's grouped collection: objects with an `items` array each.
    return Array.from(byGroup, ([group, rows]) => ({ group, items: rows }));
  }, [groupBy, items]);

  const rowKey = (row: Row<T>, index: number) =>
    isActionOption(row)
      ? 'action'
      : typeof row === 'object'
        ? `${getOptionLabel(row)}-${index}`
        : String(row);

  const renderRow = (row: Row<T>, index: number) => {
    if (isActionOption(row)) {
      return (
        <ComboboxItem key='action' value={row} className='text-primary'>
          <Plus />
          {action!.label}
          {row.inputValue && ` ${row.inputValue}`}
        </ComboboxItem>
      );
    }
    return (
      <ComboboxItem key={rowKey(row, index)} value={row} disabled={getOptionDisabled?.(row)}>
        {renderOption ? renderOption(row, { selected: isSelected(row), inputValue }) : getOptionLabel(row)}
      </ComboboxItem>
    );
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || event.isPropagationStopped()) {
      (
        event as KeyboardEvent<HTMLInputElement> & { preventBaseUIHandler: () => void }
      ).preventBaseUIHandler();
    }
  };

  const inputProps = {
    id,
    name,
    placeholder: multiple && selected.length > 0 ? undefined : placeholder,
    autoFocus,
    disabled,
    'aria-label': ariaLabel,
    'aria-invalid': error || undefined,
    'aria-describedby': helperId,
    ref: inputRef,
    onBlur,
    onFocus: (event: FocusEvent<HTMLInputElement>) => {
      if (selectOnFocus) {
        event.currentTarget.select();
      }
      if (openOnFocus && open === undefined && !innerOpen) {
        setInnerOpen(true);
        onOpen?.();
      }
      onFocus?.(event);
    },
    onKeyDown: handleKeyDown,
  };

  const defaultTags = (values: T[]) =>
    values.map((current, index) => {
      const text = getOptionLabel(current);
      return (
        <ComboboxChip key={rowKey(current, index)} aria-label={text} removeLabel={`Remove ${text}`}>
          {text}
        </ComboboxChip>
      );
    });

  const control = multiple ? (
    <ComboboxChips ref={anchor} className={cn(disabled && 'pointer-events-none opacity-50')}>
      <ComboboxValue>
        {(values: T[]) => (
          <>
            {renderTags ? renderTags(values) : defaultTags(values)}
            <ComboboxChipsInput {...inputProps} />
          </>
        )}
      </ComboboxValue>
    </ComboboxChips>
  ) : (
    <ComboboxInput
      {...inputProps}
      showTrigger={!inputEndAdornment}
      showClear={!disableClearable && !disabled}
      className='w-full'
    >
      {inputEndAdornment && (
        <InputGroupAddon align='inline-end'>
          {inputEndAdornment}
          <InputGroupButton size='icon-xs' variant='ghost' render={<ComboboxTrigger />} disabled={disabled} />
        </InputGroupAddon>
      )}
    </ComboboxInput>
  );

  return (
    <div
      ref={ref}
      data-slot='autocomplete'
      className={cn('inline-flex flex-col gap-1 align-top', fullWidth && 'flex w-full', className)}
      style={style}
    >
      {label && (
        <label htmlFor={id} className='text-xs text-muted-foreground'>
          {label}
        </label>
      )}
      <Combobox
        items={groups ?? items}
        value={value as never}
        multiple={multiple}
        disabled={disabled}
        filter={null}
        autoHighlight={autoHighlight}
        itemToStringLabel={(row: Row<T>) => (isActionOption(row) ? '' : getOptionLabel(row))}
        isItemEqualToValue={(a: Row<T>, b: Row<T>) =>
          !isActionOption(a) && !isActionOption(b) && isOptionEqualToValue(a, b)
        }
        inputValue={inputValueProp}
        onInputValueChange={(text) => {
          setTyped(text);
          onInputChange?.(text);
        }}
        open={isOpen}
        onOpenChange={(nextOpen, details) => {
          if (!nextOpen && multiple && disableCloseOnSelect && details.reason === 'item-press') {
            details.cancel();
            return;
          }
          setInnerOpen(nextOpen);
          if (nextOpen) {
            onOpen?.();
          } else {
            onClose?.();
          }
        }}
        onValueChange={(next: unknown) => {
          const picked = multiple ? (next as Row<T>[]) : [next as Row<T>];
          const actionRow = picked.find(isActionOption);
          if (actionRow) {
            action?.onAction(actionRow.inputValue || undefined);
            return;
          }
          onChange(next as AutocompleteValue<T, Multiple>);
        }}
      >
        {control}
        <ComboboxContent anchor={multiple ? anchor : undefined}>
          {action?.helperText && (
            <div className='px-2 pt-2 text-xs text-muted-foreground'>{action.helperText}</div>
          )}
          {loading && <div className='px-2 py-2 text-sm text-muted-foreground'>Loading…</div>}
          {!loading && <ComboboxEmpty>{noOptionsText}</ComboboxEmpty>}
          <ComboboxList>
            {groups
              ? (group: { group: string; items: Row<T>[] }) => (
                  <ComboboxGroup key={group.group} items={group.items}>
                    {group.group && <ComboboxLabel>{group.group}</ComboboxLabel>}
                    <ComboboxCollection>{renderRow}</ComboboxCollection>
                  </ComboboxGroup>
                )
              : renderRow}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {helperText && (
        <p id={helperId} className={cn('m-0 text-xs', error ? 'text-destructive' : 'text-muted-foreground')}>
          {helperText}
        </p>
      )}
    </div>
  );
}
